/**
 * simulator/utils/peluqueriaAgendaDemo.js
 * Agenda de demo de PELUQUERÍA como la de un salón que ya usa la app a diario (14/10/2026, David:
 * «una agenda más realista … en peluquería con intercalado»).
 *
 * En vez de citas al azar, cada profesional tiene un día de plantilla (mañana 10-14, tarde 16-19,
 * sábado solo mañana, como el horario mock de /peluqueria/user-schedule) con huecos sueltos y,
 * sobre todo, citas INTERCALADAS: mientras a una clienta le actúa el tinte o las mechas, la misma
 * profesional atiende a otra. Las citas de color llevan los mismos campos que devuelve el backend
 * (`allow_intercalation`, `intercalation_blocks`, `service_*_duration`), así que el CalendarView del
 * simulador pinta la franja «EXPOSICIÓN» y pone la cita intercalada al lado, como en la app real.
 *
 * Determinista (sin Math.random): la misma demo se ve igual en cada recarga; las plantillas rotan
 * por día y profesional para que ningún día sea calcado al anterior. Fechas relativas a hoy.
 */

import dayjs from "dayjs";

// Bloques de los servicios de color: aplicación → exposición (libre para intercalar) → lavado y peinado.
const BLOQUES = {
  tinte: [
    { type: "work", duration: 30, label: "Aplicación" },
    { type: "processing", duration: 35, label: "Exposición" },
    { type: "work", duration: 25, label: "Lavado y peinado" },
  ],
  mechas: [
    { type: "work", duration: 45, label: "Aplicación" },
    { type: "processing", duration: 40, label: "Exposición" },
    { type: "work", duration: 35, label: "Matiz, lavado y peinado" },
  ],
};

// Cómo encontrar cada tipo en el catálogo que use la demo (el mock o el real del lead).
const TIPOS = {
  tinte: { re: [/tinte|coloraci|^color\b/i], fallback: "sim-pel-s5", genero: "mujer" },
  mechas: { re: [/mechas|balayage|babylights/i], fallback: "sim-pel-s6", genero: "mujer" },
  corte_mujer: { re: [/corte.*(mujer|señora|dama)/i], fallback: "sim-pel-s1", genero: "mujer" },
  corte_caballero: { re: [/corte.*(caballero|hombre)/i], fallback: "sim-pel-s2", genero: "hombre" },
  corte_nino: { re: [/corte.*ni[ñn]/i], fallback: "sim-pel-s3", genero: "mujer" },
  corte_lavado: { re: [/corte y lavado|lavar y cortar/i], fallback: "sim-pel-s4", genero: "mujer" },
  peinado: { re: [/peinado|brushing|recogido/i], fallback: "sim-pel-s7", genero: "mujer" },
  tratamiento: { re: [/tratamiento|hidrata|keratina/i], fallback: "sim-pel-s8", genero: "mujer" },
  lavado: { re: [/^lavado/i], fallback: "sim-pel-s9", genero: "mujer" },
};

// [hora, tipo]. Las intercaladas caen dentro de la exposición de la cita de color anterior.
const PLANTILLAS_LABORABLE = [
  [
    ["10:00", "tinte"], ["10:30", "corte_caballero"], ["11:30", "corte_mujer"], ["12:15", "peinado"],
    ["13:00", "corte_nino"],
    ["16:00", "mechas"], ["16:50", "corte_caballero"], ["18:00", "tratamiento"],
  ],
  [
    ["10:00", "corte_mujer"], ["10:45", "mechas"], ["11:35", "corte_caballero"], ["12:45", "peinado"],
    ["13:30", "lavado"],
    ["16:00", "corte_lavado"], ["16:45", "tinte"], ["17:15", "corte_nino"], ["18:15", "corte_caballero"],
  ],
  [
    // Dos colores encadenados: la 2.ª aplicación va en la exposición de la 1.ª y viceversa.
    ["10:00", "tinte"], ["10:30", "tinte"], ["12:00", "corte_mujer"], ["13:00", "corte_caballero"],
    ["16:15", "peinado"], ["17:00", "corte_lavado"], ["17:45", "tratamiento"], ["18:30", "corte_caballero"],
  ],
  [
    ["10:15", "corte_caballero"], ["10:45", "corte_mujer"], ["11:30", "mechas"], ["12:15", "corte_nino"],
    ["16:00", "tinte"], ["16:30", "corte_caballero"], ["17:30", "corte_mujer"], ["18:15", "lavado"],
  ],
];
const PLANTILLAS_SABADO = [
  [["10:00", "mechas"], ["10:50", "corte_caballero"], ["12:00", "peinado"], ["12:45", "corte_mujer"]],
  [["10:00", "tinte"], ["10:30", "corte_nino"], ["11:30", "peinado"], ["12:30", "corte_lavado"], ["13:15", "corte_caballero"]],
];

const NOMBRES_HOMBRE = /^(javier|carlos|david|pedro|miguel|antonio|jos[eé]|francisco|manuel|diego|alejandro|[aá]lvaro|rafael|sergio|pablo|luis|jorge|hugo|mario)\b/i;
// "voice" = la recepcionista IA cogió el teléfono y puso la cita (15/10/2026).
const ORIGENES = ["whatsapp", "crm", "whatsapp", "voice", "web", "crm", "google"];
// La 1.ª profesional tiene sembradas en axiosClient (depositDemoEvents) las citas con anticipo: hoy a
// las 12:00, 13:00, 16:30 y 17:30 y mañana a las 11:00 y 12:30, de 30 min. Esos dos días su agenda deja
// esas medias horas libres para que no parezca una doble reserva.
export const HUECOS_ANTICIPO = { 0: ["12:00", "13:00", "16:30", "17:30"], 1: ["11:00", "12:30"] };
const PLANTILLA_HOY_PRIMERA = [
  ["10:00", "tinte"], ["10:30", "corte_caballero"], ["11:30", "corte_caballero"], ["12:30", "corte_nino"],
  ["13:30", "lavado"],
  ["16:00", "corte_caballero"], ["17:00", "corte_nino"], ["18:00", "tratamiento"],
];
const PLANTILLA_MANANA_PRIMERA = [
  ["10:00", "corte_caballero"], ["10:30", "corte_caballero"], ["11:30", "corte_mujer"], ["13:00", "corte_nino"],
  ["13:30", "lavado"],
  ["16:00", "mechas"], ["16:50", "corte_caballero"], ["18:00", "tratamiento"],
];

function buscarServicio(tipo, servicios, catalogo) {
  const { re, fallback } = TIPOS[tipo];
  for (const r of re) {
    const s = (servicios || []).find((x) => r.test(String(x?.name || x?.nombre || "")));
    if (s) return s;
  }
  return catalogo.find((s) => s.service_id === fallback || s.id === fallback) || catalogo[0];
}

const enMinutos = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/**
 * @param {object} o
 * @param {string|Date} [o.from] @param {string|Date} [o.to]
 * @param {Array} o.workers          profesionales (mock o reales del lead)
 * @param {Array} o.services         catálogo en uso (mock o real)
 * @param {Array} o.catalogo         catálogo mock de peluquería (respaldo si el real no tiene el tipo)
 * @param {string[]} o.clientNames
 * @param {(i:number,name:string)=>string} o.makePhone
 * @param {string} o.companyId
 * @param {dayjs.Dayjs} [o.now]
 */
export function buildPeluqueriaAgenda({ from, to, workers, services, catalogo, clientNames, makePhone, companyId, now }) {
  const ahora = now || dayjs();
  const hoy = ahora.startOf("day");
  const desde = dayjs(from || hoy.subtract(7, "day")).startOf("day");
  const hasta = dayjs(to || hoy.add(14, "day")).endOf("day");
  const nombres = clientNames && clientNames.length ? clientNames : ["Clienta"];
  const hombres = nombres.map((n, i) => [n, i]).filter(([n]) => NOMBRES_HOMBRE.test(n));
  const mujeres = nombres.map((n, i) => [n, i]).filter(([n]) => !NOMBRES_HOMBRE.test(n));
  const elegir = (lista, k) => {
    const l = lista.length ? lista : nombres.map((x, i) => [x, i]);
    return l[((k % l.length) + l.length) % l.length];
  };

  const events = [];
  let n = 0;
  for (let dia = desde; dia.isBefore(hasta); dia = dia.add(1, "day")) {
    const dow = dia.day();
    if (dow === 0) continue;
    const offset = dia.diff(hoy, "day");
    const plantillas = dow === 6 ? PLANTILLAS_SABADO : PLANTILLAS_LABORABLE;
    // Contadores del día compartidos entre profesionales: la misma clienta no sale dos veces el mismo día.
    let nh = offset * 5 + 50;
    let nm = offset * 7 + 50;

    for (const [wi, worker] of workers.entries()) {
      const workerId = worker.worker_id || worker.user_id || worker.id;
      const workerName = worker.name || worker.full_name || worker.username || "Profesional";
      const plantilla =
        wi === 0 && (offset === 0 || offset === 1)
          ? (offset === 0 ? PLANTILLA_HOY_PRIMERA : PLANTILLA_MANANA_PRIMERA).filter(([h]) => dow !== 6 || enMinutos(h) < 14 * 60)
          : plantillas[(((offset + wi * 2) % plantillas.length) + plantillas.length) % plantillas.length];

      for (const [k, [hora, tipo]] of plantilla.entries()) {
        const servicio = buscarServicio(tipo, services, catalogo);
        const bloques = BLOQUES[tipo] || null;
        const duracion = bloques
          ? bloques.reduce((s, b) => s + b.duration, 0)
          : Number(servicio.duration || servicio.total_duration || servicio.duracion_minutos) || 30;
        const ini = enMinutos(hora);

        const start = dia.startOf("day").add(ini, "minute");
        const end = start.add(duracion, "minute");
        const semilla = (offset + 50) * 7 + wi * 3 + k;
        const [cliente, ci] = TIPOS[tipo].genero === "hombre" ? elegir(hombres, nh++) : elegir(mujeres, nm++);
        const telefono = makePhone(ci, cliente);
        const nombreServicio = servicio.name || servicio.nombre || tipo;
        const origen = ORIGENES[(semilla + n) % ORIGENES.length];
        // Lo pasado ya vino (confirmada); de hoy en adelante, ~2 de cada 3 confirmadas.
        const confirmada = end.isBefore(ahora) || (semilla + k) % 3 !== 0;
        const id = `mock-pel-${start.valueOf()}-w${wi}-${k}`;

        const ev = {
          event_id: id,
          id,
          company_id: companyId,
          title: `${start.format("HH:mm")}-${end.format("HH:mm")}: ${nombreServicio} - ${workerName}`,
          start: start.toISOString(),
          end: end.toISOString(),
          description: bloques
            ? `Servicio: ${nombreServicio}\nProfesional: ${workerName}\n${bloques.map((b) => `${b.label} ${b.duration} min`).join(" · ")}`
            : `Servicio: ${nombreServicio}\nProfesional: ${workerName}`,
          color: servicio.color || "#8B5CF6",
          event_type: origen === "google" ? "external_google" : "manual",
          creation_source: origen === "whatsapp" || origen === "web" || origen === "voice" ? origen : undefined,
          participants: JSON.stringify([`${companyId}#${telefono}`]),
          origin_kind: "booking",
          confirmed: confirmada,
          // De vez en cuando la confirmó la IA en la llamada de recordatorio (no el botón de WhatsApp).
          confirmed_via: confirmada && semilla % 4 === 1 ? "llamada_ia" : undefined,
          service_name: nombreServicio,
          service_id: servicio.service_id || servicio.id,
          service_price: Number(servicio.price ?? servicio.precio ?? 0) || 0,
          service_duration: duracion,
          client_name: cliente,
          client_phone: telefono,
          worker_name: workerName,
          worker_id: workerId,
          resourceId: workerId,
        };
        if (bloques) {
          Object.assign(ev, {
            allow_intercalation: true,
            service_total_duration: duracion,
            service_work_duration: bloques[0].duration,
            service_calendar_duration: bloques[0].duration,
            service_processing_duration: bloques[1].duration,
            intercalation_blocks: bloques,
          });
        }
        events.push(ev);
        n++;
      }
    }
  }
  return events;
}

export const _PELUQUERIA_PLANTILLAS = { PLANTILLAS_LABORABLE, PLANTILLAS_SABADO, PLANTILLA_HOY_PRIMERA, PLANTILLA_MANANA_PRIMERA, BLOQUES };
